export type MissingTemplateVariableBehavior = 'empty' | 'error' | 'preserve';

export class TemplateFormatError extends Error {}

type ReplacementField = {
    readonly name: string;
    readonly formatSpec: string | null;
};

function findReplacementFieldEnd(template: string, startIndex: number): number {
    let nestedFieldDepth = 0;
    let escapedOpenBraces = 0;
    let index = startIndex + 1;

    while (index < template.length) {
        const character = template[index];
        if (character === '{') {
            if (template[index + 1] === '{') {
                escapedOpenBraces += 1;
                index += 2;
                continue;
            }
            nestedFieldDepth += 1;
            index += 1;
            continue;
        }
        if (character !== '}') {
            index += 1;
            continue;
        }
        if (nestedFieldDepth > 0) {
            nestedFieldDepth -= 1;
            index += 1;
            continue;
        }
        if (escapedOpenBraces > 0 && template[index + 1] === '}') {
            escapedOpenBraces -= 1;
            index += 2;
            continue;
        }
        return index;
    }

    throw new TemplateFormatError('Unclosed opening brace');
}

function parseReplacementField(field: string): ReplacementField {
    const separatorIndex = field.search(/[!:]/u);
    const name = (
        separatorIndex === -1 ? field : field.slice(0, separatorIndex)
    ).trim();
    if (!name) {
        throw new TemplateFormatError('Empty placeholder');
    }
    if (separatorIndex === -1) {
        return { name, formatSpec: null };
    }

    if (field[separatorIndex] === ':') {
        return {
            name,
            formatSpec: field.slice(separatorIndex + 1),
        };
    }

    const conversion = field[separatorIndex + 1];
    if (!conversion || !['a', 'r', 's'].includes(conversion)) {
        throw new TemplateFormatError('Unsupported conversion');
    }
    const conversionEnd = separatorIndex + 2;
    if (conversionEnd === field.length) {
        return { name, formatSpec: null };
    }
    if (field[conversionEnd] !== ':') {
        throw new TemplateFormatError('Invalid conversion');
    }
    return {
        name,
        formatSpec: field.slice(conversionEnd + 1),
    };
}

/** Extract replacement fields using the Python format syntax accepted by the API. */
export function extractPythonFormatTemplateVariables(
    template: string,
): ReadonlySet<string> {
    const variables = new Set<string>();

    const extract = (value: string) => {
        let index = 0;
        while (index < value.length) {
            const character = value[index];
            if (character === '{') {
                if (value[index + 1] === '{') {
                    index += 2;
                    continue;
                }
                const closingBrace = findReplacementFieldEnd(value, index);
                const field = parseReplacementField(
                    value.slice(index + 1, closingBrace),
                );
                variables.add(field.name);
                if (field.formatSpec) {
                    extract(field.formatSpec);
                }
                index = closingBrace + 1;
                continue;
            }
            if (character === '}') {
                if (value[index + 1] !== '}') {
                    throw new TemplateFormatError('Unmatched closing brace');
                }
                index += 2;
                continue;
            }
            index += 1;
        }
    };

    extract(template);
    return variables;
}

// Render the named-placeholder subset of Python's str.format syntax used by
// persisted message templates. Escaped braces are significant: `{{name}}`
// renders as the literal text `{name}` rather than as a variable.
export function renderPythonFormatTemplate(
    template: string,
    values: Readonly<Record<string, string>>,
    missingVariable: MissingTemplateVariableBehavior = 'error',
): string {
    let rendered = '';
    let index = 0;

    while (index < template.length) {
        const character = template[index];
        if (character === '{') {
            if (template[index + 1] === '{') {
                rendered += '{';
                index += 2;
                continue;
            }

            const closingBrace = template.indexOf('}', index + 1);
            if (closingBrace === -1) {
                throw new TemplateFormatError('Unclosed opening brace');
            }
            const variable = template.slice(index + 1, closingBrace);
            if (
                !variable ||
                variable.includes('{') ||
                /[!.:]/.test(variable) ||
                variable.includes('[') ||
                variable.includes(']')
            ) {
                throw new TemplateFormatError('Unsupported placeholder');
            }

            if (Object.prototype.hasOwnProperty.call(values, variable)) {
                rendered += values[variable] ?? '';
            } else if (missingVariable === 'preserve') {
                rendered += `{${variable}}`;
            } else if (missingVariable === 'error') {
                throw new TemplateFormatError(`Unknown variable: ${variable}`);
            }
            index = closingBrace + 1;
            continue;
        }

        if (character === '}') {
            if (template[index + 1] !== '}') {
                throw new TemplateFormatError('Unmatched closing brace');
            }
            rendered += '}';
            index += 2;
            continue;
        }

        rendered += character;
        index += 1;
    }

    return rendered;
}
